<script setup lang="ts">
import { resetForm } from '@/helpers/ep/form'
import type { IServiceForm } from '@/types/rc/forms'
import { type FormInstance, type FormRules } from 'element-plus'
import { computed, reactive, ref } from 'vue'
import type { IService } from '@/types/rc/work-orders'
import { useServices } from '@/composables/rc/services'
import { Icon } from '@iconify/vue'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import axios from 'axios'
import { NotifyFail } from '@/helpers/core/notifications'

const iconOptions = ref<OptionType[]>([])
const iconSearchLoading = ref<boolean>(false)

const { createServiceMutation, updateServiceMutation } = useServices()
const {
  isPending: createIsPending,
  mutateAsync: createAsync,
  isError: createIsError,
  error: createError,
} = createServiceMutation

const {
  isPending: updateIsPending,
  mutateAsync: updateAsync,
  isError: updateIsError,
  error: updateError,
} = updateServiceMutation

const open = defineModel<boolean>()
const props = defineProps<{ serviceToUpdate?: IService }>()
const emit = defineEmits(['closed'])

const serviceFormRef = ref<FormInstance>()
const serviceForm = reactive<IServiceForm>({
  description: '',
  icon: '',
  price: undefined,
})

const rules = reactive<FormRules<IServiceForm>>({
  description: [{ required: true, message: 'Description is required.', trigger: 'blur' }],
  price: [{ required: true, message: 'Price is required.', trigger: 'blur' }],
  icon: [{ required: true, message: 'Icon is required.', trigger: 'blur' }],
})

/**
 * Return form loading state (used to disable form).
 */
const formLoading = computed<boolean>(() => {
  return createIsPending.value || updateIsPending.value
})

/**
 * Handles form submit.
 * Create or updates a service, depending on the form mode.
 *
 * @param formEl form element ref.
 */
const submitForm = async (formEl: FormInstance | undefined) => {
  formEl?.validate(async (valid) => {
    if (!valid) {
      return
    }

    try {
      if (props.serviceToUpdate) {
        await updateAsync({ serviceID: props.serviceToUpdate.id, form: serviceForm })
        open.value = false
      } else {
        await createAsync({ ...serviceForm })
        open.value = false
      }
    } catch {
      // handled in UI
    }
  })
}

const searchIcons = async (query?: string) => {
  const trimmedQuery = query?.trim()

  if (trimmedQuery == '') return

  try {
    iconSearchLoading.value = true

    const queryParams = {
      query: trimmedQuery,
      prefixes: 'mdi,material-symbols',
      limit: 100,
    }

    const response = await axios.get('https://api.iconify.design/search', { params: queryParams })
    if (response.status != axios.HttpStatusCode.Ok) {
      throw Error('Error fetching icons')
    }

    const blacklist = ['poop']

    iconOptions.value = response.data.icons
      .filter((icon: string) => blacklist.some((term) => !icon.includes(term)))
      .map((icon: string) => {
        return {
          label: icon,
          value: icon,
        }
      })
  } catch {
    NotifyFail('Failed to filter icons')
    iconOptions.value = []
  } finally {
    iconSearchLoading.value = false
  }
}

/**
 * Reset form
 */
const handleClosed = () => {
  resetForm(serviceFormRef.value)
  createServiceMutation.reset()
  updateServiceMutation.reset()
  iconOptions.value = []
  emit('closed')
}

const initForm = () => {
  if (props.serviceToUpdate) {
    Object.assign(serviceForm, props.serviceToUpdate)
  }
}
</script>

<template>
  <el-dialog
    v-model="open"
    :width="600"
    :title="`${serviceToUpdate ? 'Update' : 'Create'} Service`"
    :show-close="false"
    @closed="handleClosed"
    @open="initForm"
  >
    <el-form
      ref="serviceFormRef"
      :model="serviceForm"
      :rules="rules"
      :label-width="160"
      label-position="left"
      :disabled="formLoading"
    >
      <el-form-item label="Description" prop="description">
        <el-input v-model="serviceForm.description" placeholder="Description" clearable />
      </el-form-item>
      <el-form-item label="Price" prop="price">
        <el-currency-input v-model="serviceForm.price" clearable />
      </el-form-item>
      <el-form-item label="Icon" prop="icon">
        <el-select-v2
          v-model="serviceForm.icon"
          :options="iconOptions"
          filterable
          remote
          :remote-method="searchIcons"
          clearable
          :loading="iconSearchLoading"
          no-data-text="No icons associated to your input were found"
          placeholder="Search for an icon"
          value-key="value"
        >
          <template #default="{ item }">
            <div class="flex items-center gap-3">
              <Icon :icon="item.value" :height="20" />
              <span>{{ item.label }}</span>
            </div>
          </template>
          <template #label="{ label, value }">
            <div class="flex items-center gap-3">
              <Icon :icon="value" :height="20" />
              <span>{{ label }}</span>
            </div>
          </template>
          <template #loading>
            <div class="remote-search--loading">
              <Icon icon="eos-icons:three-dots-loading" height="20" /></div
          ></template>
        </el-select-v2>
      </el-form-item>
    </el-form>
    <rl-error-alert v-if="createIsError" :error="createError" />
    <rl-error-alert v-if="updateIsError" :error="updateError" />
    <template #footer>
      <el-button @click="open = false">Cancel</el-button>
      <el-button
        type="primary"
        :loading="createIsPending || updateIsPending"
        :disabled="formLoading || !open"
        @click="submitForm(serviceFormRef)"
        >Submit</el-button
      >
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.remote-search--loading {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
